"use client";

import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { type FormEvent, useState } from "react";
import type { TagNamespace } from "@/lib/schemas/products";

// SearchFilters drives the /search page entirely through the URL: the name
// query is `?q=` and each selected facet is a repeated `?tag=` param. On submit
// or facet toggle it router.replace()s the new querystring, which re-runs the
// server component's fetch. Selection state is derived from props (the URL), so
// this stays in sync with back/forward navigation.
export function SearchFilters({
	vocabulary,
	q,
	selected,
}: {
	vocabulary: TagNamespace[];
	q: string;
	selected: string[];
}) {
	const router = useRouter();
	const [text, setText] = useState(q);
	const selectedSet = new Set(selected);

	function navigate(nextQ: string, tags: string[]) {
		const sp = new URLSearchParams();
		if (nextQ) sp.set("q", nextQ);
		for (const t of tags) sp.append("tag", t);
		const qs = sp.toString();
		router.replace(qs ? `/search?${qs}` : "/search");
	}

	function submitSearch(e: FormEvent) {
		e.preventDefault();
		if (text.trim()) {
			posthog.capture("search_performed", {
				query: text.trim(),
				active_filters: selected,
				filter_count: selected.length,
			});
		}
		navigate(text, selected);
	}

	function toggleTag(tag: string) {
		const tags = selectedSet.has(tag)
			? selected.filter((t) => t !== tag)
			: [...selected, tag];
		navigate(text, tags);
	}

	function clearAll() {
		setText("");
		navigate("", []);
	}

	const hasFilters = selected.length > 0 || q.length > 0;

	return (
		<aside className="space-y-6">
			<form onSubmit={submitSearch} className="space-y-2">
				<label
					htmlFor="search-q"
					className="block font-mono text-mono-sm uppercase tracking-[var(--tracking-mono)] text-muted"
				>
					Search
				</label>
				<div className="flex gap-2">
					<input
						id="search-q"
						type="search"
						value={text}
						onChange={(e) => setText(e.target.value)}
						placeholder="Name, animal, bike…"
						className="h-9 w-full border border-ink/20 bg-paper px-3 text-body-sm text-ink placeholder:text-muted focus:border-ink focus:outline-none"
					/>
					<button
						type="submit"
						className="h-9 shrink-0 border border-ink px-3 font-mono text-mono-xs uppercase tracking-[var(--tracking-mono)] text-ink hover:bg-ink hover:text-paper"
					>
						Go
					</button>
				</div>
			</form>

			{hasFilters && (
				<button
					type="button"
					onClick={clearAll}
					className="font-mono text-mono-xs uppercase tracking-[var(--tracking-mono)] text-muted underline hover:text-ink"
				>
					Clear all
				</button>
			)}

			{vocabulary.map((ns) =>
				ns.options.length === 0 ? null : (
					<fieldset key={ns.namespace} className="space-y-2">
						<legend className="font-mono text-mono-sm uppercase tracking-[var(--tracking-mono)] text-muted">
							{ns.label}
						</legend>
						<div className="space-y-1.5">
							{ns.options.map((opt) => (
								<label
									key={opt.tag}
									className="flex cursor-pointer items-center gap-2 text-body-sm text-ink"
								>
									<input
										type="checkbox"
										checked={selectedSet.has(opt.tag)}
										onChange={() => toggleTag(opt.tag)}
										className="h-3.5 w-3.5 accent-accent"
									/>
									{opt.label}
								</label>
							))}
						</div>
					</fieldset>
				),
			)}
		</aside>
	);
}
